import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { Button, Card, CardBody, Col, Container, DropdownItem, DropdownMenu, DropdownToggle, Row, UncontrolledDropdown } from 'reactstrap'
import properties from '../../../api/properties/hooks'
import settings from '../../../api/settings/hooks'
import { MODAL_TYPES, PROPERTY_STATUS } from '../../../common/constants'
import AppPermissionsSwitcher from '../../../Components/AppPermissionsSwitcher/AppPermissionsSwitcher'
import { action } from '../../../Components/AppPermissionsSwitcher/constants/actions'
import PageTitle from '../../../Components/Common/PageTitle'
import { setupDeleteCallback } from '../../../Components/Modals/DeleteModal/utils'
import QueryErrorBoundary from '../../../Components/QueryErrorBoundary/QueryErrorBoundary'
import { DataTableLayout, useDataTable, useRowClick, useTableState } from '../../../Components/TanstackTable'
import { showModal } from '../../../store/modal'
import { selectSearchCriteriaProperties } from '../../../store/search'
import { selectCheckedTableRows } from '../../../store/table'
import { useTableColumns } from '../hooks'
import { getFilteredProperty } from '../utils'
import { getModalPayloads } from './listModalPayloads'
import MapProperty from './MapProperty/MapProperty'

const PropertyList = () => {
    const history = useHistory()
    const { t } = useTranslation()
    const propertySearchCriteria = useSelector(selectSearchCriteriaProperties)

    const tableState = useTableState({
        tableKey: 'property',
        initialPage: history.location.state?.page,
    })

    const queryParams = {
        ...tableState.params,
        criteria: propertySearchCriteria,
    }

    return (
        <div className="page-content">
            <Container fluid>
                <PageTitle title={t('app.common.properties')} />
                <Row>
                    <Col lg={12}>
                        <Card id="RealEstatesList">
                            <CardBody>
                                <QueryErrorBoundary resetKeys={[JSON.stringify(queryParams)]}>
                                    <PropertyTable tableState={tableState} queryParams={queryParams} />
                                </QueryErrorBoundary>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

// Owns the query that throws on error, plus everything that depends on its result
const PropertyTable = ({ tableState, queryParams }) => {
    const location = useLocation()
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { DELETE_MODAL, OFFER_BASE_MODAL, TEMPLATES_MODAL, CHANGE_AGENT_MODAL, EXPORT_ADS_MODAL, EXPORT_DATA_MODAL, PROPERTY_PRINT_FORM_MODAL } =
        MODAL_TYPES

    const [showMap, setShowMap] = useState(false)
    const selectedTableRows = useSelector(selectCheckedTableRows)

    const getPropertiesConfig = {
        params: { ...queryParams },
        isEnabled: {
            enabled: Boolean(tableState.params.page && tableState.params.limit && Object.keys(queryParams.criteria ?? {}).length),
            throwOnError: true,
        },
    }

    const { isLoading, data: propertiesData } = properties.GetProperties.useQuery(getPropertiesConfig.params, getPropertiesConfig.isEnabled)
    const { mutate: deleteProperties } = properties.DeleteProperties.useMutation()
    const { data: applicationMarket } = settings.GetApplicationSettings.useQuery({
        select: (data) => data.applicationMarket,
    })

    const handleShowHideMap = () => {
        setShowMap(!showMap)
    }

    const handleModal = (modalType) => {
        const payload = getModalPayloads({
            modalType,
            selectedTableRows,
            params: queryParams,
            columns,
        })

        if (!payload) return

        setupDeleteCallback(modalType, payload.modalProps, (ids) => deleteProperties({ ids }))
        dispatch(showModal(payload))
    }

    const propertyItems = useMemo(() => propertiesData?.items ?? [], [propertiesData?.items])

    const onRowClick = useRowClick('/properties')
    const columns = useTableColumns()

    const { table, updateColumnSizing } = useDataTable({
        ...tableState,
        columns,
        data: propertyItems,
        pageCount: propertiesData?.pagination?.totalPages,
        selectRow: {
            mapRow: getFilteredProperty,
        },
    })

    const isExportAdsModalAllowed =
        selectedTableRows.length > 0 && selectedTableRows.every((row) => row.propertyStatus?.id === PROPERTY_STATUS.ACTIVE_OFFER)

    const IS_HR_MARKET = applicationMarket?.name === 'Hrvatska'

    const tableActions = () => (
        <div className="d-flex flex-grow-1">
            <Button tag={Link} to="/properties/new" color="add" className="btn-label">
                <i className="mdi mdi-domain align-bottom label-icon align-middle fs-16 me-2"></i>
                {t('button.add')}
            </Button>
            <UncontrolledDropdown className="ms-2">
                <DropdownToggle title={t('table.showMore')} href="#" className="btn btn-primary dropdown" tag="button">
                    <i className="ri-more-fill align-middle"></i>
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-end">
                    <AppPermissionsSwitcher permission="ModulePermissions" module="properties" action={action.delete}>
                        {({ hasModulePermission }) =>
                            hasModulePermission && (
                                <DropdownItem onClick={() => handleModal(DELETE_MODAL)} disabled={!selectedTableRows.length}>
                                    <i className="mdi mdi-trash-can-outline align-bottom me-2 text-muted"></i> {t('table.action.deleteSelected')}
                                </DropdownItem>
                            )
                        }
                    </AppPermissionsSwitcher>
                    <DropdownItem onClick={() => handleModal(OFFER_BASE_MODAL)} disabled={!selectedTableRows.length}>
                        <i className="mdi mdi-file-document-multiple-outline align-bottom me-2 text-muted"></i> {t('app.common.offerOrPrint')}
                    </DropdownItem>
                    <DropdownItem onClick={() => handleModal(TEMPLATES_MODAL)} disabled={!selectedTableRows.length}>
                        <i className="mdi mdi-file-document-multiple-outline align-bottom me-2 text-muted"></i> {t('table.action.shopWindowPrint')}
                    </DropdownItem>
                    <AppPermissionsSwitcher permission="ChangeAgent">
                        {({ hasPermission }) =>
                            hasPermission && (
                                <DropdownItem onClick={() => handleModal(CHANGE_AGENT_MODAL)} disabled={!selectedTableRows.length}>
                                    <i className="mdi mdi-account-switch-outline align-bottom me-2 text-muted"></i> {t('app.common.switchAgent')}
                                </DropdownItem>
                            )
                        }
                    </AppPermissionsSwitcher>
                    {IS_HR_MARKET && (
                        <DropdownItem onClick={() => handleModal(PROPERTY_PRINT_FORM_MODAL)}>
                            <i className="mdi mdi-export me-2 text-muted"></i> Formular za unos nekretnine
                        </DropdownItem>
                    )}
                    <DropdownItem onClick={() => handleModal(EXPORT_ADS_MODAL)} disabled={!selectedTableRows.length || !isExportAdsModalAllowed}>
                        <i className="mdi mdi-export me-2 text-muted"></i> {t('app.common.exportPortalAds')}
                    </DropdownItem>
                    <DropdownItem onClick={handleShowHideMap}>
                        <i className={`mdi ${showMap ? 'mdi-table' : 'mdi-google-maps'} align-bottom me-2 text-muted`}></i> {t('table.action.show')}{' '}
                        {showMap ? t('table.action.show.table') : t('table.action.show.map')}
                    </DropdownItem>
                    <AppPermissionsSwitcher permission="ExportData">
                        {({ hasPermission }) =>
                            hasPermission && (
                                <DropdownItem onClick={() => handleModal(EXPORT_DATA_MODAL)}>
                                    <i className="mdi mdi-export align-bottom me-2 text-muted"></i> {t('app.common.exportData.title')}
                                </DropdownItem>
                            )
                        }
                    </AppPermissionsSwitcher>
                </DropdownMenu>
            </UncontrolledDropdown>
        </div>
    )

    const handlePermissions = ({ row, children }) => {
        const agent = row.original.primaryAgent

        return (
            <AppPermissionsSwitcher
                key={row.id}
                permission="ModulePermissions"
                itemAgentId={agent?.id}
                itemAgentOfficeId={agent?.agencyOffice?.id}
                module={location}
                action={action.view}
            >
                {({ hasModulePermission }) =>
                    children({
                        onClick: (e) => hasModulePermission && onRowClick(row.original.id, e, tableState.params.page),
                        style: { cursor: hasModulePermission ? 'pointer' : 'not-allowed' },
                    })
                }
            </AppPermissionsSwitcher>
        )
    }

    return showMap ? (
        <MapProperty tableActions={tableActions} properties={propertiesData?.items} isDataLoading={isLoading} />
    ) : (
        <DataTableLayout
            table={table}
            isLoading={isLoading}
            totalItems={propertiesData?.pagination?.totalItems}
            hasColumnSelection
            hasPageSizeSelect
            theadClass="dmn-table-header"
            toolbarActions={tableActions}
            updateColumnSizing={updateColumnSizing}
            renderRow={handlePermissions}
        />
    )
}

export default PropertyList
